import React from 'react';
import { Smartphone, Check, Star } from 'lucide-react';

const MobileApps: React.FC = () => {
  const adminFeatures = ['Real-time fleet overview', 'Approve trips & expenses', 'Instant alerts', 'Performance reports'];
  const driverFeatures = ['Trip assignments', 'Status updates', 'Document upload', 'Earnings tracking'];

  return (
    <section className="section-padding bg-gray-50">
      <div className="container-main">
        <div className="text-center max-w-3xl mx-auto mb-12 lg:mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4 lg:mb-6 font-heading">
            Your Fleet, in Your <span className="text-primary-600">Pocket</span>
          </h2>
          <p className="text-lg text-gray-600">
            Powerful mobile apps for fleet managers and drivers. Stay connected to your operations anywhere, anytime.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Features Side */}
          <div className="order-2 lg:order-1 space-y-6">
            <div className="card">
              <div className="p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-navy-950 font-heading">Admin & Fleet Manager App</h3>
                    <p className="text-sm text-gray-500">Complete control on the go</p>
                  </div>
                </div>
                <ul className="space-y-3 mb-6">
                  {adminFeatures.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <div className="w-5 h-5 bg-accent-green/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-accent-green" />
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span className="font-medium text-navy-950">4.8</span>
                  <span>App Store Rating</span>
                </div>
              </div>
            </div>
            
            <div className="card">
              <div className="p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-accent-orange/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-6 h-6 text-accent-orange" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-navy-950 font-heading">Driver Mobile App</h3>
                    <p className="text-sm text-gray-500">Simple, intuitive design</p>
                  </div>
                </div>
                <ul className="space-y-3 mb-6">
                  {driverFeatures.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <div className="w-5 h-5 bg-accent-green/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <Check className="w-3 h-3 text-accent-green" />
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span className="font-medium text-navy-950">4.9</span>
                  <span>Play Store Rating</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Phone Mockups Side */}
          <div className="order-1 lg:order-2">
            <div className="relative flex justify-center items-center gap-4 lg:gap-6">
              {/* Admin App Phone */}
              <div className="w-36 sm:w-44 lg:w-56 bg-navy-950 rounded-[1.5rem] lg:rounded-[2rem] p-2 shadow-sm transform -rotate-6 hover:rotate-0 transition-transform duration-500">
                <div className="aspect-[9/16] bg-gradient-to-b from-gray-800 to-gray-900 rounded-[1.25rem] lg:rounded-[1.75rem] flex items-center justify-center">
                  <div className="text-center p-4">
                    <div className="w-12 h-12 lg:w-16 lg:h-16 bg-primary-600 rounded-xl lg:rounded-2xl flex items-center justify-center mx-auto mb-3 lg:mb-4">
                      <Smartphone className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
                    </div>
                    <p className="text-white font-semibold font-heading text-sm lg:text-base">Admin App</p>
                    <p className="text-gray-400 text-xs lg:text-sm mt-1 lg:mt-2">Fleet Dashboard</p>
                  </div>
                </div>
              </div>
              
              {/* Driver App Phone */}
              <div className="w-36 sm:w-44 lg:w-56 bg-navy-950 rounded-[1.5rem] lg:rounded-[2rem] p-2 shadow-sm transform rotate-6 hover:rotate-0 transition-transform duration-500 mt-8 lg:mt-12">
                <div className="aspect-[9/16] bg-gradient-to-b from-gray-800 to-gray-900 rounded-[1.25rem] lg:rounded-[1.75rem] flex items-center justify-center">
                  <div className="text-center p-4">
                    <div className="w-12 h-12 lg:w-16 lg:h-16 bg-accent-orange rounded-xl lg:rounded-2xl flex items-center justify-center mx-auto mb-3 lg:mb-4">
                      <Smartphone className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
                    </div>
                    <p className="text-white font-semibold font-heading text-sm lg:text-base">Driver App</p>
                    <p className="text-gray-400 text-xs lg:text-sm mt-1 lg:mt-2">Trip Management</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* App Store Buttons */}
            <div className="mt-8 lg:mt-12 flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 px-4">
              <button className="bg-black text-white px-5 py-3 rounded-xl flex items-center justify-center gap-3 hover:bg-gray-800 transition-colors">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs">Download on the</div>
                  <div className="text-sm font-semibold font-heading">App Store</div>
                </div>
              </button>
              <button className="bg-black text-white px-5 py-3 rounded-xl flex items-center justify-center gap-3 hover:bg-gray-800 transition-colors">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs">Get it on</div>
                  <div className="text-sm font-semibold font-heading">Google Play</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MobileApps;
